<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">{{ isEdit ? 'Editar combo' : 'Nuevo combo' }}</h1>
          <p class="view-header__subtitle">El precio se recalcula siempre con el precio actual de cada producto — no se guarda fijo.</p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-md-8 d-flex">
        <div class="sale-detail combo-left">
          <div class="card products-card mb-2">
            <div class="products-card__header">
              <div class="products-card__search">
                <receipt-products-search
                  :separator="'*'"
                  :show-barcodes="true"
                  :product_default="null"
                  :price-list="pricelistId"
                  @productadded="onProductAdded"
                />
              </div>
            </div>

            <div class="products-card__body">
              <table v-if="products.length > 0" class="table products-table table-striped">
                <thead>
                  <tr>
                    <th class="products-table__num">#</th>
                    <th>Código</th>
                    <th>Producto</th>
                    <th class="text-right">Precio actual</th>
                    <th class="text-center">Cantidad</th>
                    <th v-if="discountEachLine" class="text-center products-table__discount-col">Descuento</th>
                    <th class="text-right">Subtotal</th>
                    <th class="text-center products-table__actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(product, index) in products.slice().reverse()" :key="product.id">
                    <td class="products-table__num">{{ products.length - index }}</td>
                    <td>
                      <div class="d-flex flex-column products-table__codes text-left">
                        <small v-if="product.code" class="text-muted" title="Código interno">
                          <i class="bi bi-hash mr-1"></i>{{ product.code }}
                        </small>
                        <small v-if="product.barcode" class="text-muted" title="Código de barra">
                          <i class="bi bi-upc-scan mr-1"></i>{{ product.barcode }}
                        </small>
                      </div>
                    </td>
                    <td>{{ product.name }}</td>
                    <td class="text-right">{{ formatoDinamicoMoneda(product.price, 'display') }}</td>
                    <td class="text-center">
                      <div class="qty-stepper">
                        <button type="button" class="qty-stepper__btn" :disabled="product.quantity <= 1" @click="decrementQty(product)">−</button>
                        <input v-model.number="product.quantity" @change="clampQty(product)" min="1" type="number" class="qty-stepper__input">
                        <button type="button" class="qty-stepper__btn" @click="incrementQty(product)">+</button>
                      </div>
                    </td>
                    <td v-if="discountEachLine" class="text-center">
                      <div class="input-group input-group-sm products-table__discount-edit">
                        <input v-model.number="product.discount" @change="clampDiscount(product)" min="0" max="100" type="number" class="form-control text-right">
                        <div class="input-group-append">
                          <span class="input-group-text">%</span>
                        </div>
                      </div>
                    </td>
                    <td class="text-right products-table__total">{{ formatoDinamicoMoneda(lineTotal(product), 'display') }}</td>
                    <td class="text-center">
                      <button type="button" class="btn btn-outline-danger btn-sm products-table__delete" title="Quitar" @click="removeProduct(products.length - 1 - index)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-else class="products-card__empty">
                <i class="bi bi-box-seam"></i>
                <h5>Todavía no hay productos</h5>
                <p class="text-muted small mb-0">Buscá con la lupa o ingresá el código arriba.</p>
              </div>
            </div>
          </div>

          <div v-if="products.length > 0" class="products-summary">
            <span class="products-summary__count">
              {{ productsCount }} {{ productsCount === 1 ? 'producto' : 'productos' }} <i class="bi bi-cart"></i>
            </span>
            <span class="products-summary__items">
              Items: <strong>{{ itemsCount }}</strong> <i class="bi bi-box"></i>
            </span>
            <div class="products-summary__right">
              <button type="button" class="btn btn-outline-danger btn-sm" @click="cleanAll()">
                <i class="bi bi-trash"></i> Vaciar todo
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-4">
        <div class="form-card">
          <div class="form-card__body">
            <div class="form-card__field">
              <label class="form-card__label">Nombre</label>
              <input v-model="name" type="text" class="form-control">
            </div>
            <div class="form-card__field">
              <label class="form-card__label">Descripción</label>
              <textarea v-model="description" class="form-control"></textarea>
            </div>
            <div class="form-card__field form-card__field--switch">
              <label class="form-card__label">
                <input v-model="active" type="checkbox">
                Combo activo
              </label>
            </div>
          </div>
        </div>

        <div v-if="products.length > 0" class="sale-detail">
          <div class="card totals-card mb-2">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Totales del combo</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Subtotal</span>
                <span class="value">{{ formatoDinamicoMoneda(subtotal, 'display') }}</span>
              </div>
              <div class="total-row totals-card__discount-row">
                <span class="label">Descuento</span>
                <span v-if="discountEachLine" class="value text-success">
                  <template v-if="discountTotal > 0">− </template>{{ formatoDinamicoMoneda(discountTotal, 'display') }}
                </span>
                <span v-else class="totals-card__discount-right">
                  <input
                    v-model.number="globalDiscount"
                    @change="clampGlobalDiscount"
                    type="number"
                    min="0"
                    class="form-control form-control-sm totals-card__discount-input"
                    placeholder="0"
                  >
                  <div class="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      class="btn"
                      :class="globalDiscountType === 'percentage' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="setGlobalDiscountType('percentage')"
                    >%</button>
                    <button
                      type="button"
                      class="btn"
                      :class="globalDiscountType === 'amount' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="setGlobalDiscountType('amount')"
                    >$</button>
                  </div>
                  <span class="value text-success">
                    <template v-if="discountTotal > 0">− </template>{{ formatoDinamicoMoneda(discountTotal, 'display') }}
                  </span>
                </span>
              </div>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatoDinamicoMoneda(total, 'display') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex flex-column" style="gap: 10px;">
          <button type="button" class="btn view-header__btn view-header__btn--primary view-header__btn--xl justify-content-center" :disabled="!canSave" @click="save">
            Guardar combo
          </button>
          <router-link :to="{ name: 'Combos' }" class="btn view-header__btn view-header__btn--xl justify-content-center">Cancelar</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import ReceiptProductsSearch from "../pos-document/receipt/Receipt-products-search.vue";

export default {
  props: ['bundleId', 'budgetId'],
  mixins: [formatMoneyMixin],
  components: {
    ReceiptProductsSearch,
  },
  data: function () {
    return {
      name: '',
      description: '',
      active: true,
      products: [],
      saving: false,
      // Descuento global del combo: solo se usa (y se muestra) cuando el
      // descuento por linea esta desactivado para la sucursal.
      globalDiscount: 0,
      globalDiscountType: 'percentage',
    };
  },
  computed: {
    ...mapState('authUser', { authUser: state => state.authUser }),
    // El payload de getAuthUser trae branchoffices como array (ver Products-list.vue / Subnavbar-ventas).
    pricelistId() {
      const bo = this.authUser && this.authUser.branchoffices && this.authUser.branchoffices[0];
      return bo && bo.configs ? bo.configs.pricelist_id : null;
    },
    // Igual que en el POS: con descuento por linea activado, cada producto
    // tiene el suyo. Desactivado, es un unico descuento para todo el combo.
    discountEachLine() {
      const bo = this.authUser && this.authUser.branchoffices && this.authUser.branchoffices[0];
      return !!(bo && bo.options && bo.options.discount_each_line);
    },
    isEdit() {
      return !!this.bundleId;
    },
    canSave() {
      return !this.saving
        && this.name.trim().length > 0
        && this.products.length > 0
        && this.products.every((product) => product.quantity > 0);
    },
    subtotal() {
      return this.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
    },
    globalDiscountAmount() {
      if (this.discountEachLine) return 0;
      const value = parseFloat(this.globalDiscount) || 0;
      return this.globalDiscountType === 'amount' ? value : (this.subtotal * value / 100);
    },
    total() {
      if (this.discountEachLine) {
        return this.products.reduce((sum, product) => sum + this.lineTotal(product), 0);
      }
      return Math.max(0, this.subtotal - this.globalDiscountAmount);
    },
    discountTotal() {
      return this.discountEachLine ? (this.subtotal - this.total) : this.globalDiscountAmount;
    },
    productsCount() {
      return this.products.length;
    },
    itemsCount() {
      return this.products.reduce((sum, product) => sum + (parseFloat(product.quantity) || 0), 0);
    },
  },
  mounted() {
    if (this.isEdit) {
      this.loadBundle();
    } else if (this.budgetId) {
      this.loadFromBudget();
    }
  },
  methods: {
    loadBundle() {
      axios.get('/api/bundles/getBundle/' + this.bundleId).then((response) => {
        const bundle = response.data.data;
        this.name = bundle.name;
        this.description = bundle.description;
        this.active = !!bundle.active;
        this.globalDiscount = parseFloat(bundle.discount) || 0;
        this.globalDiscountType = bundle.discount_type === 'amount' ? 'amount' : 'percentage';

        const requests = bundle.bundle_details.map((detail) => (
          axios.get('/api/products/getProduct/' + detail.product_id + '/' + this.pricelistId).then((productResponse) => ({
            id: detail.product_id,
            name: productResponse.data.data.name,
            code: productResponse.data.data.code,
            barcode: productResponse.data.data.barcode,
            price: parseFloat(productResponse.data.data.price),
            quantity: detail.quantity,
            discount: parseFloat(detail.discount) || 0,
          }))
        ));

        Promise.all(requests).then((products) => {
          this.products = products;
        }).catch(() => {
          alert('No se pudieron cargar todos los productos del combo. Puede que alguno haya sido eliminado.');
        });
      }).catch(() => {
        alert('No se pudo cargar el combo.');
        this.$router.push({ name: 'Combos' }).catch(() => {});
      });
    },
    loadFromBudget() {
      axios.get('/api/budgets/getBudget/' + this.budgetId).then((response) => {
        const budget = response.data.data;
        const pos = budget.pos;
        const number = budget.number;
        this.name = (pos && number)
          ? `Combo presupuesto #${String(pos).padStart(4, '0')}-${String(number).padStart(8, '0')}`
          : `Combo presupuesto #${this.budgetId}`;

        const details = budget.budgetDetails || budget.budget_details || [];
        const requests = details.map((detail) => (
          axios.get('/api/products/getProduct/' + detail.product_id + '/' + this.pricelistId).then((productResponse) => ({
            id: detail.product_id,
            name: productResponse.data.data.name,
            code: productResponse.data.data.code,
            barcode: productResponse.data.data.barcode,
            price: parseFloat(productResponse.data.data.price),
            quantity: detail.quantity,
            discount: 0,
          }))
        ));

        Promise.all(requests).then((products) => {
          this.products = products;
        }).catch(() => {
          alert('No se pudieron cargar todos los productos del presupuesto. Puede que alguno haya sido eliminado.');
        });
      }).catch(() => {
        alert('No se pudo cargar el presupuesto.');
        this.$router.push({ name: 'Combos' }).catch(() => {});
      });
    },
    onProductAdded(product) {
      const existing = this.products.find((item) => item.id === product.id);
      if (existing) {
        existing.quantity += product.quantity;
        return;
      }
      this.products.push({
        id: product.id,
        name: product.name,
        code: product.code,
        barcode: product.barcode,
        price: parseFloat(product.price),
        quantity: product.quantity,
        discount: 0,
      });
    },
    removeProduct(index) {
      this.products.splice(index, 1);
    },
    incrementQty(product) {
      product.quantity = (parseFloat(product.quantity) || 0) + 1;
    },
    decrementQty(product) {
      product.quantity = Math.max(1, (parseFloat(product.quantity) || 1) - 1);
    },
    clampQty(product) {
      const q = parseFloat(product.quantity);
      if (isNaN(q) || q < 1) product.quantity = 1;
    },
    clampDiscount(product) {
      const d = parseFloat(product.discount);
      if (isNaN(d) || d < 0) product.discount = 0;
      if (d > 100) product.discount = 100;
    },
    lineTotal(product) {
      const discount = parseFloat(product.discount) || 0;
      return product.quantity * (product.price - (discount * product.price / 100));
    },
    setGlobalDiscountType(type) {
      this.globalDiscountType = type;
      this.clampGlobalDiscount();
    },
    clampGlobalDiscount() {
      let d = parseFloat(this.globalDiscount);
      if (isNaN(d) || d < 0) d = 0;
      if (this.globalDiscountType === 'percentage' && d > 100) d = 100;
      if (this.globalDiscountType === 'amount' && d > this.subtotal) d = this.subtotal;
      this.globalDiscount = d;
    },
    cleanAll() {
      if (confirm('¿Está seguro que desea borrar todos los productos?')) {
        this.products = [];
      }
    },
    save() {
      if (this.saving) return;
      this.saving = true;

      const payload = {
        name: this.name,
        description: this.description,
        active: this.active,
        discount: this.discountEachLine ? 0 : (this.globalDiscount || 0),
        discount_type: this.globalDiscountType,
        products: this.products.map((product) => ({ id: product.id, quantity: product.quantity, discount: this.discountEachLine ? (product.discount || 0) : 0 })),
      };

      const request = this.isEdit
        ? axios.post('/api/bundles/edit/' + this.bundleId, payload)
        : axios.post('/api/bundles/add', payload);

      request.then(() => {
        this.$router.push({ name: 'Combos' }).catch(() => {});
      }).catch(() => {
        alert('No se pudo guardar el combo. Revisá los datos e intentá de nuevo.');
        this.saving = false;
      });
    },
  },
};
</script>

<style scoped>
/* Pisa el height: calc(100vh - 110px) de .sale-detail (pensado para el
   layout full-viewport del POS real) en los DOS usos de la clase en esta
   pantalla (tabla de productos a la izquierda, card de totales a la
   derecha): acá el alto lo da el row de Bootstrap (align-items: stretch)
   entre columnas, no el viewport. */
.sale-detail {
  height: auto;
  min-height: 0;
}

.combo-left {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.combo-left .products-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.combo-left .products-card__body {
  flex: 1;
  height: auto;
  min-height: 0;
}
</style>
